import React, { useState, useEffect } from 'react';
import { 
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, PieChart, Pie, Cell, Legend, AreaChart, Area 
} from 'recharts';
import { 
  Calendar, Download, Printer, TrendingUp, Package, Egg, 
  ChevronDown, ChevronUp, BarChart3, Activity, Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COMPANY_NAME = "Wilyrash Farm";
const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_egg-inventory-pro/artifacts/vtgmvoj5_Logo%201.jpg";

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
const EGG_COLORS = {
  extra_large: '#8b5cf6',
  large: '#3b82f6', 
  medium: '#10b981',
  small: '#f59e0b',
  damaged: '#ef4444'
};

// Helper to get pen label (7 = Brooding Pen, 8 = Grower Pen)
const getPenLabel = (pen) => {
  if (pen === 7) return 'Brooding Pen';
  if (pen === 8) return 'Grower Pen';
  return `Pen ${pen}`;
};

const ProductionReports = () => {
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(true);
  const [reportType, setReportType] = useState('daily'); // daily, weekly, monthly
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [productionData, setProductionData] = useState([]);
  const [penMetrics, setPenMetrics] = useState([]);
  const [summaryStats, setSummaryStats] = useState(null);
  const [showPenDetails, setShowPenDetails] = useState(false);

  useEffect(() => {
    fetchProductionReports();
  }, [dateRange, reportType]);

  const fetchProductionReports = async () => {
    setLoading(true);
    try {
      // Fetch production data for date range
      const response = await fetch(
        `${API_URL}/api/production-reports?start_date=${dateRange.start}&end_date=${dateRange.end}&report_type=${reportType}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setProductionData(data.daily_data || []);
        setPenMetrics(data.pen_metrics || []);
        setSummaryStats(data.summary || null);
      }
    } catch (error) {
      console.error('Error fetching production reports:', error);
      toast.error('Failed to load production reports');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => (num || 0).toLocaleString();

  // Calculate totals for display
  const calculateTotals = () => {
    if (!productionData.length) return null;
    
    const totals = {
      extra_large_crates: 0, extra_large_pieces: 0,
      large_crates: 0, large_pieces: 0,
      medium_crates: 0, medium_pieces: 0,
      small_crates: 0, small_pieces: 0,
      damaged_crates: 0, damaged_pieces: 0,
      total_crates: 0, total_pieces: 0
    };

    productionData.forEach(day => {
      ['extra_large', 'large', 'medium', 'small', 'damaged'].forEach(size => {
        totals[`${size}_crates`] += day[`${size}_crates`] || 0;
        totals[`${size}_pieces`] += day[`${size}_pieces`] || 0;
        totals.total_crates += day[`${size}_crates`] || 0;
        totals.total_pieces += day[`${size}_pieces`] || 0;
      });
    });

    return totals;
  };

  // Export to CSV
  const exportToCSV = () => {
    let csvContent = 'Date,Extra Large Crates,Extra Large Pieces,Large Crates,Large Pieces,Medium Crates,Medium Pieces,Small Crates,Small Pieces,Damaged Crates,Damaged Pieces,Total Crates,Total Pieces\n';
    
    productionData.forEach(day => {
      const totalCrates = (day.extra_large_crates || 0) + (day.large_crates || 0) + (day.medium_crates || 0) + (day.small_crates || 0) + (day.damaged_crates || 0);
      const totalPieces = (day.extra_large_pieces || 0) + (day.large_pieces || 0) + (day.medium_pieces || 0) + (day.small_pieces || 0) + (day.damaged_pieces || 0);
      
      csvContent += `${day.date},${day.extra_large_crates || 0},${day.extra_large_pieces || 0},${day.large_crates || 0},${day.large_pieces || 0},${day.medium_crates || 0},${day.medium_pieces || 0},${day.small_crates || 0},${day.small_pieces || 0},${day.damaged_crates || 0},${day.damaged_pieces || 0},${totalCrates},${totalPieces}\n`;
    });

    // Add pen metrics
    csvContent += '\n\nPen Performance Metrics\n';
    csvContent += 'Pen,Total Crates,Total Pieces,Avg Daily Crates,Days Active,Efficiency Rank\n';
    penMetrics.forEach((pen, idx) => {
      csvContent += `${getPenLabel(pen.pen_number)},${pen.total_crates || 0},${pen.total_pieces || 0},${(pen.avg_daily_crates || 0).toFixed(1)},${pen.days_active || 0},${idx + 1}\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `production_report_${dateRange.start}_to_${dateRange.end}.csv`;
    link.click();
    toast.success('Report exported successfully');
  };

  // Print PDF
  const handlePrintReport = () => {
    const totals = calculateTotals();
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${COMPANY_NAME} - Production Report</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Segoe UI', sans-serif; padding: 30px; color: #333; }
            .container { max-width: 900px; margin: 0 auto; }
            .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #10b981; }
            .company-info { display: flex; align-items: center; gap: 15px; }
            .company-logo { width: 60px; height: 60px; border-radius: 50%; object-fit: cover; }
            .company-name { font-size: 20px; font-weight: bold; color: #10b981; }
            .report-title h1 { font-size: 22px; }
            .report-date { font-size: 12px; color: #666; }
            .summary-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px; margin-bottom: 30px; }
            .summary-card { background: #f8fafc; padding: 15px; border-radius: 8px; text-align: center; }
            .summary-label { font-size: 10px; color: #666; text-transform: uppercase; }
            .summary-value { font-size: 18px; font-weight: bold; }
            .section { margin-bottom: 30px; }
            .section h3 { font-size: 14px; margin-bottom: 15px; padding-bottom: 5px; border-bottom: 1px solid #e2e8f0; }
            table { width: 100%; border-collapse: collapse; font-size: 11px; }
            th { background: #10b981; color: white; padding: 8px; text-align: left; }
            td { padding: 6px 8px; border-bottom: 1px solid #e2e8f0; }
            tr:nth-child(even) { background: #f8fafc; }
            .pen-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; }
            .pen-card { background: #f8fafc; padding: 15px; border-radius: 8px; }
            .pen-header { font-weight: bold; margin-bottom: 10px; display: flex; justify-content: space-between; }
            .pen-rank { background: #10b981; color: white; padding: 2px 8px; border-radius: 10px; font-size: 10px; }
            .footer { margin-top: 40px; text-align: center; font-size: 11px; color: #666; border-top: 1px solid #e2e8f0; padding-top: 20px; }
            @media print { body { padding: 15px; } }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <div class="company-info">
                <img src="${COMPANY_LOGO}" alt="Logo" class="company-logo" />
                <div>
                  <div class="company-name">${COMPANY_NAME}</div>
                  <div style="font-size: 11px; color: #666;">Production Report</div>
                </div>
              </div>
              <div class="report-title">
                <h1>${reportType.charAt(0).toUpperCase() + reportType.slice(1)} Production Report</h1>
                <div class="report-date">${dateRange.start} to ${dateRange.end}</div>
              </div>
            </div>
            
            ${totals ? `
            <div class="summary-grid">
              <div class="summary-card">
                <div class="summary-label">Extra Large</div>
                <div class="summary-value">${formatNumber(totals.extra_large_crates)}c</div>
                <div style="font-size: 11px; color: #666;">${formatNumber(totals.extra_large_pieces)}p</div>
              </div>
              <div class="summary-card">
                <div class="summary-label">Large</div>
                <div class="summary-value">${formatNumber(totals.large_crates)}c</div>
                <div style="font-size: 11px; color: #666;">${formatNumber(totals.large_pieces)}p</div>
              </div>
              <div class="summary-card">
                <div class="summary-label">Medium</div>
                <div class="summary-value">${formatNumber(totals.medium_crates)}c</div>
                <div style="font-size: 11px; color: #666;">${formatNumber(totals.medium_pieces)}p</div>
              </div>
              <div class="summary-card">
                <div class="summary-label">Small</div>
                <div class="summary-value">${formatNumber(totals.small_crates)}c</div>
                <div style="font-size: 11px; color: #666;">${formatNumber(totals.small_pieces)}p</div>
              </div>
              <div class="summary-card" style="background: #fef2f2;">
                <div class="summary-label">Damaged</div>
                <div class="summary-value" style="color: #dc2626;">${formatNumber(totals.damaged_crates)}c</div>
                <div style="font-size: 11px; color: #666;">${formatNumber(totals.damaged_pieces)}p</div>
              </div>
            </div>
            ` : ''}
            
            <div class="section">
              <h3>Pen Performance Rankings</h3>
              <div class="pen-grid">
                ${penMetrics.map((pen, idx) => `
                  <div class="pen-card">
                    <div class="pen-header">
                      <span>${pen.pen_number === 7 ? 'Brooding Pen' : `Pen ${pen.pen_number}`}</span>
                      <span class="pen-rank">#${idx + 1}</span>
                    </div>
                    <div style="font-size: 12px;">
                      <div>Total: ${formatNumber(pen.total_crates)} crates, ${formatNumber(pen.total_pieces)} pieces</div>
                      <div>Avg/Day: ${(pen.avg_daily_crates || 0).toFixed(1)} crates</div>
                      <div>Active Days: ${pen.days_active || 0}</div>
                    </div>
                  </div>
                `).join('')}
              </div>
            </div>
            
            <div class="section">
              <h3>Daily Production Details</h3>
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>X-Large</th>
                    <th>Large</th>
                    <th>Medium</th>
                    <th>Small</th>
                    <th>Damaged</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${productionData.map(day => {
                    const total = (day.extra_large_crates || 0) + (day.large_crates || 0) + (day.medium_crates || 0) + (day.small_crates || 0) + (day.damaged_crates || 0);
                    return `
                      <tr>
                        <td>${day.date}</td>
                        <td>${day.extra_large_crates || 0}c / ${day.extra_large_pieces || 0}p</td>
                        <td>${day.large_crates || 0}c / ${day.large_pieces || 0}p</td>
                        <td>${day.medium_crates || 0}c / ${day.medium_pieces || 0}p</td>
                        <td>${day.small_crates || 0}c / ${day.small_pieces || 0}p</td>
                        <td>${day.damaged_crates || 0}c / ${day.damaged_pieces || 0}p</td>
                        <td><strong>${total}c</strong></td>
                      </tr>
                    `;
                  }).join('')}
                </tbody>
              </table>
            </div>
            
            <div class="footer">
              <p>Generated by ${COMPANY_NAME} Management System • ${new Date().toLocaleString('en-GB', { timeZone: 'Africa/Lagos', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}</p>
            </div>
          </div>
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const totals = calculateTotals();

  // Prepare chart data
  const chartData = productionData.map(day => ({
    date: day.date?.substring(5) || '',
    'Extra Large': day.extra_large_crates || 0,
    'Large': day.large_crates || 0,
    'Medium': day.medium_crates || 0,
    'Small': day.small_crates || 0,
    'Damaged': day.damaged_crates || 0,
    total: (day.extra_large_crates || 0) + (day.large_crates || 0) + (day.medium_crates || 0) + (day.small_crates || 0) + (day.damaged_crates || 0)
  }));

  // Pie chart data for size distribution
  const sizeDistribution = totals ? [
    { name: 'Extra Large', value: totals.extra_large_crates, color: EGG_COLORS.extra_large },
    { name: 'Large', value: totals.large_crates, color: EGG_COLORS.large },
    { name: 'Medium', value: totals.medium_crates, color: EGG_COLORS.medium },
    { name: 'Small', value: totals.small_crates, color: EGG_COLORS.small },
    { name: 'Damaged', value: totals.damaged_crates, color: EGG_COLORS.damaged }
  ].filter(item => item.value > 0) : [];

  return (
    <div className="space-y-6" data-testid="production-reports-page">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Production Reports
          </h1>
          <p className="text-lg text-slate-600 mt-2">Analyze egg production trends and pen performance</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportToCSV} data-testid="export-csv-button">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrintReport} data-testid="print-pdf-button">
            <Printer className="w-4 h-4 mr-2" />
            Print PDF
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium">Report Type:</Label>
            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger className="w-32" data-testid="report-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-500" />
            <Label className="text-sm">From:</Label>
            <Input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              className="w-40"
              data-testid="start-date"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm">To:</Label>
            <Input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              className="w-40"
              data-testid="end-date"
            />
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            { label: 'Extra Large', crates: totals.extra_large_crates, pieces: totals.extra_large_pieces, color: 'bg-purple-100 text-purple-700 border-purple-200' },
            { label: 'Large', crates: totals.large_crates, pieces: totals.large_pieces, color: 'bg-blue-100 text-blue-700 border-blue-200' },
            { label: 'Medium', crates: totals.medium_crates, pieces: totals.medium_pieces, color: 'bg-green-100 text-green-700 border-green-200' },
            { label: 'Small', crates: totals.small_crates, pieces: totals.small_pieces, color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
            { label: 'Damaged', crates: totals.damaged_crates, pieces: totals.damaged_pieces, color: 'bg-red-100 text-red-700 border-red-200' },
            { label: 'Total', crates: totals.total_crates, pieces: totals.total_pieces, color: 'bg-emerald-100 text-emerald-700 border-emerald-200' }
          ].map(stat => (
            <div key={stat.label} className={`rounded-xl border-2 p-4 ${stat.color}`}>
              <div className="text-xs font-medium uppercase tracking-wider opacity-70">{stat.label}</div>
              <div className="text-2xl font-bold mt-1">{formatNumber(stat.crates)}</div>
              <div className="text-sm opacity-80">crates</div>
              <div className="text-lg font-semibold">{formatNumber(stat.pieces)}</div>
              <div className="text-xs opacity-70">pieces</div>
            </div>
          ))}
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Production Trend Chart */}
        <div className="card">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-500" />
            Production Trend (Crates)
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="Extra Large" stackId="1" stroke={EGG_COLORS.extra_large} fill={EGG_COLORS.extra_large} fillOpacity={0.6} />
                <Area type="monotone" dataKey="Large" stackId="1" stroke={EGG_COLORS.large} fill={EGG_COLORS.large} fillOpacity={0.6} />
                <Area type="monotone" dataKey="Medium" stackId="1" stroke={EGG_COLORS.medium} fill={EGG_COLORS.medium} fillOpacity={0.6} />
                <Area type="monotone" dataKey="Small" stackId="1" stroke={EGG_COLORS.small} fill={EGG_COLORS.small} fillOpacity={0.6} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Size Distribution Pie Chart */}
        <div className="card">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-500" />
            Size Distribution
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={sizeDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  dataKey="value"
                >
                  {sizeDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Pen Performance Metrics */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Award className="w-5 h-5 text-yellow-500" />
            Pen Performance Rankings
          </h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowPenDetails(!showPenDetails)}
          >
            {showPenDetails ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
            {showPenDetails ? 'Hide' : 'Show'} Details
          </Button>
        </div>

        {/* Pen Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {penMetrics.map((pen, idx) => (
            <div
              key={pen.pen_number}
              className={`rounded-xl p-4 border-2 ${
                idx === 0 ? 'bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-300' :
                idx === 1 ? 'bg-gradient-to-br from-slate-50 to-slate-100 border-slate-300' :
                idx === 2 ? 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-300' :
                'bg-white border-slate-200'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-lg">{getPenLabel(pen.pen_number)}</span>
                <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                  idx === 0 ? 'bg-yellow-500 text-white' :
                  idx === 1 ? 'bg-slate-400 text-white' :
                  idx === 2 ? 'bg-orange-400 text-white' :
                  'bg-slate-200 text-slate-600'
                }`}>
                  #{idx + 1}
                </span>
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Crates:</span>
                  <span className="font-semibold">{formatNumber(pen.total_crates)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Pieces:</span>
                  <span className="font-semibold">{formatNumber(pen.total_pieces)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Avg/Day:</span>
                  <span className="font-semibold">{(pen.avg_daily_crates || 0).toFixed(1)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Active Days:</span>
                  <span className="font-semibold">{pen.days_active || 0}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Detailed Pen Comparison Chart */}
        {showPenDetails && penMetrics.length > 0 && (
          <div className="mt-6 pt-6 border-t">
            <h4 className="font-medium mb-4">Pen Comparison (Total Crates)</h4>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={penMetrics}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="pen_number" tickFormatter={(val) => val === 7 ? 'Brood' : val === 8 ? 'Grower' : `Pen ${val}`} />
                  <YAxis />
                  <Tooltip formatter={(value) => [formatNumber(value), 'Crates']} />
                  <Bar dataKey="total_crates" fill="#10b981" radius={[4, 4, 0, 0]}>
                    {penMetrics.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Daily Production Table */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Activity className="w-5 h-5 text-emerald-500" />
          Daily Production Details
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="production-table">
            <thead>
              <tr className="border-b bg-slate-50">
                <th className="text-left py-3 px-4">Date</th>
                <th className="text-right py-3 px-2">X-Large</th>
                <th className="text-right py-3 px-2">Large</th>
                <th className="text-right py-3 px-2">Medium</th>
                <th className="text-right py-3 px-2">Small</th>
                <th className="text-right py-3 px-2">Damaged</th>
                <th className="text-right py-3 px-4 font-bold">Total</th>
              </tr>
            </thead>
            <tbody>
              {productionData.map((day, idx) => {
                const total = (day.extra_large_crates || 0) + (day.large_crates || 0) + (day.medium_crates || 0) + (day.small_crates || 0) + (day.damaged_crates || 0);
                return (
                  <tr key={idx} className="border-b hover:bg-slate-50">
                    <td className="py-2 px-4 font-medium">{day.date}</td>
                    <td className="text-right py-2 px-2">
                      <span className="font-semibold">{day.extra_large_crates || 0}</span>
                      <span className="text-slate-400 text-xs ml-1">/{day.extra_large_pieces || 0}p</span>
                    </td>
                    <td className="text-right py-2 px-2">
                      <span className="font-semibold">{day.large_crates || 0}</span>
                      <span className="text-slate-400 text-xs ml-1">/{day.large_pieces || 0}p</span>
                    </td>
                    <td className="text-right py-2 px-2">
                      <span className="font-semibold">{day.medium_crates || 0}</span>
                      <span className="text-slate-400 text-xs ml-1">/{day.medium_pieces || 0}p</span>
                    </td>
                    <td className="text-right py-2 px-2">
                      <span className="font-semibold">{day.small_crates || 0}</span>
                      <span className="text-slate-400 text-xs ml-1">/{day.small_pieces || 0}p</span>
                    </td>
                    <td className="text-right py-2 px-2 text-red-600">
                      <span className="font-semibold">{day.damaged_crates || 0}</span>
                      <span className="text-red-400 text-xs ml-1">/{day.damaged_pieces || 0}p</span>
                    </td>
                    <td className="text-right py-2 px-4 font-bold text-emerald-600">{total}c</td>
                  </tr>
                );
              })}
              {productionData.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-slate-500">
                    No production data for selected date range
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProductionReports;
